import { PageIcon } from "@/components/PageIcon";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, Globe, RefreshCw, Settings2, LayoutGrid } from "lucide-react";
import { formatDateTime } from "@/lib/datetime";
import { RangeBar, presetRange } from "@/components/ga/RangeBar";
import { LayoutPanel } from "@/components/ga/LayoutPanel";
import { Box, Kpis, Enquiries, Trend, DataTable, Channels, Woo, QualityBar, History, SECTION_LABELS } from "@/components/ga/GaSections";

const errMsg = (e) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Could not load report");
const UTM_LABELS = { sessionManualSource: "UTM source", sessionManualMedium: "UTM medium", sessionManualCampaignName: "UTM campaign", sessionManualTerm: "UTM term", sessionManualAdContent: "UTM content" };

function Section({ id, r, layout, history }) {
  const t = SECTION_LABELS[id];
  switch (id) {
    case "quality": return <QualityBar items={r.quality} />;
    case "kpis": return <Kpis r={r} kpis={layout.kpis} />;
    case "enquiries": return <Box title={t} testid="ga-sec-enquiries"><Enquiries r={r} /><p className="text-[11px] text-slate-400 mt-2">Only confirmed mappings count as qualified enquiries. GA4 key events are not treated as enquiries.</p></Box>;
    case "trend": return <Box title={t} testid="ga-sec-trend"><Trend r={r} grain={layout.grain} /></Box>;
    case "channels": return <Box title={t} testid="ga-sec-channels"><Channels r={r} /></Box>;
    case "woo": return <Box title={t} testid="ga-sec-woo"><Woo r={r} /></Box>;
    case "sources": return <Box title={t} testid="ga-sec-sources"><DataTable rows={r.sources} dims={["sessionSourceMedium"]} labels={["Source / medium"]} testid="ga-sources-table" /></Box>;
    case "campaigns": return <Box title={t} testid="ga-sec-campaigns"><DataTable rows={r.campaigns} dims={["sessionCampaignName", "sessionSourceMedium"].filter((d) => !r.campaigns || d in (r.campaigns[0] || { sessionCampaignName: 1 }))} labels={["Campaign", "Source / medium"]} testid="ga-campaigns-table" /></Box>;
    case "utm": return <Box title={t} testid="ga-sec-utm"><DataTable rows={r.utm} dims={r.utm_dims} labels={r.utm_dims.map((d) => UTM_LABELS[d])} testid="ga-utm-table" /></Box>;
    case "landing": return <Box title={t} testid="ga-sec-landing"><DataTable rows={r.landing} dims={["landingPage"]} labels={["Landing page"]} testid="ga-landing-table" /></Box>;
    case "enquiry_pages": return <Box title={t} testid="ga-sec-enquiry-pages">{r.enquiry_pages ? <DataTable rows={r.enquiry_pages} dims={["pagePath"]} labels={["Page"]} testid="ga-enquiry-pages-table" /> : <p className="text-sm text-slate-400">Confirm at least one enquiry mapping to see the pages where enquiries happen.</p>}</Box>;
    case "pages": return <Box title={t} testid="ga-sec-pages"><DataTable rows={r.pages} dims={["pagePath"]} labels={["Page"]} testid="ga-pages-table" /><p className="text-[11px] text-slate-400 mt-2">Exit pages aren't available in the GA4 Data API.</p></Box>;
    case "events": return <Box title={t} testid="ga-sec-events"><DataTable rows={r.events} dims={["eventName"]} labels={["Event"]} testid="ga-events-table" /></Box>;
    case "history": return <Box title={t} testid="ga-sec-history"><History months={history} /></Box>;
    default: return null;
  }
}

export default function WebsiteAnalyticsPage() {
  const { can } = useAuth();
  const admin = can("settings.manage");
  const [cfg, setCfg] = useState(null);
  const [range, setRange] = useState(null);
  const [compare, setCompare] = useState("previous");
  const [layout, setLayout] = useState(null);
  const [st, setSt] = useState(null);
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [history, setHistory] = useState([]);
  const [panel, setPanel] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/integrations/google-analytics/status").then((r) => setSt(r.data)).catch(() => setSt({}));
    api.get("/ga4/layout").then((r) => setLayout(r.data)).catch(() => {});
    api.get("/ga4/history").then((r) => setHistory(r.data.months)).catch(() => {});
    api.get("/ga4/config").then((r) => { setCfg(r.data); setRange(presetRange(r.data.settings.default_range)); setCompare(r.data.settings.compare); }).catch(() => setRange(presetRange("28")));
  }, []);
  const load = useCallback((refresh = false) => {
    if (!range) return;
    setErr(""); if (refresh) setBusy(true); else setD(null);
    api.get("/ga4/report", { params: { start: range.start, end: range.end, compare, refresh } }).then((r) => setD(r.data))
      .catch((e) => { setErr(errMsg(e)); setD((x) => x || { report: null }); }).finally(() => setBusy(false));
  }, [range, compare]);
  useEffect(() => { load(); }, [load]);
  const saveLayout = async (l) => {
    try { await api.put("/ga4/layout", { order: l.order, hidden: l.hidden, kpis: l.kpis, grain: l.grain }); setLayout({ ...layout, ...l }); setPanel(false); toast.success("Layout saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const r = d?.report;
  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8 space-y-5" data-testid="website-analytics-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px]">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-3"><PageIcon />Website Analytics</h1>
          <p className="text-sm text-slate-500 mt-1" data-testid="ga-report-meta">{st?.property_name ? `GA4 · ${st.property_name}` : "Google Analytics 4"}{r?.synced_at ? ` · synced ${formatDateTime(r.synced_at)}` : ""}{r?.excluded_dates?.length ? ` · ${r.excluded_dates.length} date(s) excluded` : ""}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="gap-1.5" onClick={() => setPanel(true)} disabled={!layout} data-testid="ga-customise-btn"><LayoutGrid className="w-4 h-4" />Customise</Button>
          {admin && <Button variant="outline" className="gap-1.5" onClick={() => load(true)} disabled={busy || !r} data-testid="ga-refresh-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}Refresh</Button>}
          {admin && <Button asChild className="gap-1.5" data-testid="ga-configure-btn"><Link to="/marketing/website-analytics/configure"><Settings2 className="w-4 h-4" />Configure Analytics</Link></Button>}
        </div>
      </div>
      {range && (
        <div className="flex flex-wrap items-center gap-2">
          <RangeBar range={range} setRange={setRange} />
          <select value={compare} onChange={(e) => setCompare(e.target.value)} className="h-9 rounded-xl border border-slate-200 bg-white px-2 text-xs" data-testid="ga-compare-select">
            <option value="none">No comparison</option><option value="previous">vs previous period</option><option value="yoy">vs same period last year</option>
          </select>
        </div>
      )}
      {(err || st?.last_error) && <p className="text-sm text-rose-700 bg-rose-50 rounded-xl px-4 py-3" data-testid="ga-report-error">{err || st.last_error}</p>}
      {!d || !layout ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : r ? (
        <div className="space-y-6">{layout.order.filter((s) => !layout.hidden.includes(s)).map((s) => <Section key={s} id={s} r={r} layout={layout} history={history} />)}</div>
      ) : !err && (
        <p className="text-sm text-slate-500" data-testid="ga-report-empty">Google Analytics isn't connected for this company yet. <Link to="/settings/integrations" className="text-indigo-600 underline">Open Integrations</Link></p>
      )}
      {layout && panel && <LayoutPanel open={panel} onOpenChange={setPanel} layout={layout} onSave={saveLayout} />}
    </div>
  );
}
